// Pure logic: turns a fal input schema (as served by
// GET /v1/generate/:model_id/schema, see podstack-core-backend
// services/inference-service task-2-report.md) into a dynamic form model,
// and back into a request body. No I/O, no React.
//
// Served property shape (per the backend contract):
//   Primitive:       { type, title?, description?, enum?, default?, minimum?, maximum?, format? }
//   Primitive array:  { type: 'array', title?, description?, items: { type, enum? } }
//   Object / array-of-object / depth-exceeded: { type: 'object'|'array', jsonOnly: true, title?, description? }

export type SchemaWidget =
  | 'textarea'
  | 'text'
  | 'url'
  | 'number'
  | 'integer'
  | 'select'
  | 'boolean'
  | 'chips'
  | 'json';

export interface SchemaField {
  name: string;
  widget: SchemaWidget;
  label: string;
  description?: string;
  required: boolean;
  enumValues?: string[];
  // Set when a select's enum is numeric (integer/number type): enumValues
  // hold the stringified options for the <select>, and the chosen value is
  // coerced back to a number at body-build time.
  enumNumeric?: boolean;
  defaultValue?: unknown;
  min?: number;
  max?: number;
  step?: number;
  accept?: string;
}

// Server-computed billing/metering parameters. These are never
// user-editable via the dynamic form - the playground derives them itself
// (see utils/generation.ts estimateQuantity) - so they're dropped entirely
// rather than rendered as a field.
const METERING_PARAMS = new Set(['resolution', 'duration', 'num_frames']);

const URL_ACCEPT_BY_NAME: Record<string, string> = {
  video_url: 'video/*',
  audio_url: 'audio/*',
  image_url: 'image/*',
};

function snakeToTitleCase(name: string): string {
  return name
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface RawProperty {
  type?: string;
  title?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
  format?: string;
  jsonOnly?: boolean;
  items?: { type?: string; enum?: unknown[] };
}

function determineWidget(name: string, prop: RawProperty): SchemaWidget {
  if (name === 'prompt' || name === 'text') return 'textarea';
  if (prop.format === 'uri' || name.endsWith('_url')) return 'url';
  if (prop.type === 'string' && Array.isArray(prop.enum)) return 'select';
  // Numeric enums (e.g. LTX's duration [6,8,10] / fps [24,25,48,50]) are
  // fixed choices, not free numbers - a plain number input would let users
  // submit values fal rejects after the fact.
  if ((prop.type === 'integer' || prop.type === 'number') && Array.isArray(prop.enum)) return 'select';
  if (prop.type === 'integer') return 'integer';
  if (prop.type === 'number') return 'number';
  if (prop.type === 'boolean') return 'boolean';
  if (
    prop.type === 'array' &&
    !prop.jsonOnly &&
    prop.items &&
    (prop.items.type === 'string' || prop.items.type === 'number')
  ) {
    return 'chips';
  }
  if (prop.jsonOnly) return 'json';
  if (prop.type === 'string') return 'text';
  // Unknown/missing type (and any other array/object shape) - render as raw JSON.
  return 'json';
}

function buildField(name: string, prop: RawProperty, requiredSet: Set<string>): SchemaField {
  const widget = determineWidget(name, prop);
  const field: SchemaField = {
    name,
    widget,
    label: prop.title || snakeToTitleCase(name),
    required: requiredSet.has(name),
  };
  if (prop.description) field.description = prop.description;
  if (typeof prop.minimum === 'number') field.min = prop.minimum;
  if (typeof prop.maximum === 'number') field.max = prop.maximum;
  if (Object.prototype.hasOwnProperty.call(prop, 'default')) field.defaultValue = prop.default;

  if (widget === 'select' && Array.isArray(prop.enum)) {
    field.enumValues = prop.enum.map(String);
    if (prop.type === 'integer' || prop.type === 'number') {
      field.enumNumeric = true;
      // The <select> compares against string option values, so the seeded
      // default must be a string too; submit coerces it back.
      if (typeof field.defaultValue === 'number') field.defaultValue = String(field.defaultValue);
    }
  }
  if (widget === 'chips' && prop.items?.enum && Array.isArray(prop.items.enum)) {
    field.enumValues = prop.items.enum as string[];
  }
  if (widget === 'integer') {
    field.step = 1;
  } else if (widget === 'number') {
    field.step = typeof field.max === 'number' && field.max <= 1 ? 0.05 : 0.1;
  }
  if (widget === 'url') {
    const accept = URL_ACCEPT_BY_NAME[name];
    if (accept) field.accept = accept;
  }
  return field;
}

// Parses a served fal input schema ({"properties": {...}, "required": [...]})
// into an ordered list of form fields. Ordering: prompt/text field(s) first,
// then required fields (in the schema's own `required` array order), then
// everything else in properties declaration order. Metering params
// (resolution, duration, num_frames) are excluded entirely. Anything that
// isn't a well-formed schema object returns [].
export function parseInputSchema(schema: unknown): SchemaField[] {
  if (typeof schema !== 'object' || schema === null) return [];
  const obj = schema as { properties?: Record<string, RawProperty>; required?: unknown };
  if (typeof obj.properties !== 'object' || obj.properties === null) return [];
  const properties = obj.properties;
  const required = Array.isArray(obj.required) ? (obj.required as string[]) : [];
  const requiredSet = new Set(required);

  const names = Object.keys(properties).filter((n) => !METERING_PARAMS.has(n));

  const promptFirst = names.filter((n) => n === 'prompt' || n === 'text');
  const placed = new Set(promptFirst);

  const requiredOrdered = required.filter((n) => names.includes(n) && !placed.has(n));
  requiredOrdered.forEach((n) => placed.add(n));

  const rest = names.filter((n) => !placed.has(n));

  const orderedNames = [...promptFirst, ...requiredOrdered, ...rest];
  return orderedNames.map((name) => buildField(name, properties[name], requiredSet));
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

// Builds a request body from form field values. Optional fields with an
// empty value (undefined/null/"") are omitted; required fields are always
// included (missing-required validation is a separate concern, see
// missingRequired). json widgets parse their string value, throwing an
// Error carrying the field's label on invalid JSON; number/integer widgets
// are coerced with Number(); everything else (text/textarea/url/select/
// boolean/chips) passes through untouched.
export function buildBodyFromSchemaValues(
  fields: SchemaField[],
  values: Record<string, unknown>
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.name];
    const empty = isEmptyValue(raw);

    if (!field.required && empty) continue;
    if (empty) {
      // Required but empty - include as-is; validation is missingRequired's job.
      body[field.name] = raw;
      continue;
    }

    if (field.widget === 'json') {
      if (typeof raw === 'string') {
        try {
          body[field.name] = JSON.parse(raw);
        } catch {
          throw new Error(`Invalid JSON for ${field.label}`);
        }
      } else {
        body[field.name] = raw;
      }
    } else if (field.widget === 'number' || field.widget === 'integer') {
      body[field.name] = typeof raw === 'number' ? raw : Number(raw);
    } else if (field.widget === 'select' && field.enumNumeric) {
      body[field.name] = typeof raw === 'number' ? raw : Number(raw);
    } else {
      body[field.name] = raw;
    }
  }
  return body;
}

// Labels of required fields whose value is empty (undefined/null/""). false
// is a valid value for a required boolean field; 0 is a valid value for a
// required number field - neither is reported as missing.
export function missingRequired(fields: SchemaField[], values: Record<string, unknown>): string[] {
  return fields.filter((f) => f.required && isEmptyValue(values[f.name])).map((f) => f.label);
}
